/**
 * Local IndexedDB store for chat data. A port of mobile
 * `chat_local_db.dart` (`ChatLocalDb`, SQLite) to the web platform.
 *
 * Same contract: the chat list UI reads from the local store first
 * (Signal/WhatsApp pattern), a background sync keeps it in step with the
 * backend, and the sender outbox survives restarts so interrupted sends
 * are retried instead of silently lost.
 *
 * Row shapes keep mobile's epoch-millisecond integers field-for-field
 * (see `toMap` over there); converters translate to the ISO-string
 * domain types in `./types`.
 */
import type { ChatMessage, Conversation, MessageStatus } from './types'

export const CHAT_DB_NAME = 'lm-chat'
export const CHAT_DB_VERSION = 3

const STORES = {
  conversations: 'conversations',
  messages: 'messages',
  outbox: 'outbox',
  events: 'chat_events',
  sync: 'chat_sync',
} as const

/** Flat row mirroring mobile `ChatDbRow.toMap`. */
export interface ConversationRow {
  id: string
  other_participant_id: string
  other_participant_username: string
  other_participant_display_name: string
  other_participant_avatar_url?: string
  last_message?: string
  last_message_at?: number
  unread_count: number
  updated_at: number
  is_online: 0 | 1
}

/** Flat row mirroring mobile `ChatDbMessageRow.toMap`. */
export interface MessageRow {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  image_url?: string
  is_read: 0 | 1
  client_message_id?: string
  status: MessageStatus
  sent_at?: number
  delivered_at?: number
  read_at?: number
  created_at: number
}

/** Flat row mirroring mobile `ChatDbOutboxRow.toMap`. */
export interface OutboxRow {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  image_url?: string
  created_at: number
  attempts: number
}

function epoch(iso: string | undefined): number | undefined {
  if (!iso) return undefined
  const t = Date.parse(iso)
  return Number.isNaN(t) ? undefined : t
}

function iso(ms: number | undefined | null): string | undefined {
  return typeof ms === 'number' ? new Date(ms).toISOString() : undefined
}

export function conversationToRow(c: Conversation): ConversationRow {
  return {
    id: c.id,
    other_participant_id: c.otherUser.id,
    other_participant_username: c.otherUser.username,
    other_participant_display_name: c.otherUser.displayName,
    other_participant_avatar_url: c.otherUser.avatarUrl,
    last_message: c.lastMessage,
    last_message_at: epoch(c.lastMessageAt),
    unread_count: c.unreadCount,
    updated_at: Date.parse(c.updatedAt),
    is_online: c.otherUser.isOnline ? 1 : 0,
  }
}

export function conversationFromRow(r: ConversationRow): Conversation {
  return {
    id: r.id,
    otherUser: {
      id: r.other_participant_id,
      username: r.other_participant_username,
      displayName: r.other_participant_display_name,
      avatarUrl: r.other_participant_avatar_url,
      isOnline: r.is_online === 1,
    },
    lastMessage: r.last_message,
    lastMessageAt: iso(r.last_message_at),
    unreadCount: r.unread_count ?? 0,
    updatedAt: new Date(r.updated_at).toISOString(),
  }
}

function statusOf(value: unknown, isRead: boolean): MessageStatus {
  if (isRead) return 'read'
  if (value === 'sending' || value === 'sent' || value === 'delivered' || value === 'read') return value
  return 'sent'
}

export function messageToRow(m: ChatMessage): MessageRow {
  return {
    id: m.id,
    conversation_id: m.conversationId,
    sender_id: m.senderId,
    content: m.content,
    image_url: m.imageUrl,
    is_read: m.isRead ? 1 : 0,
    client_message_id: m.clientMessageId,
    status: m.status,
    sent_at: epoch(m.sentAt),
    delivered_at: epoch(m.deliveredAt),
    read_at: epoch(m.readAt),
    created_at: Date.parse(m.createdAt),
  }
}

export function messageFromRow(r: MessageRow): ChatMessage {
  const isRead = (r.is_read ?? 0) === 1
  return {
    id: r.id,
    conversationId: r.conversation_id,
    senderId: r.sender_id,
    content: r.content,
    imageUrl: r.image_url,
    isRead,
    clientMessageId: r.client_message_id,
    status: statusOf(r.status, isRead),
    sentAt: iso(r.sent_at),
    deliveredAt: iso(r.delivered_at),
    readAt: iso(r.read_at),
    createdAt: new Date(r.created_at).toISOString(),
  }
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'))
  })
}

function createSchema(db: IDBDatabase): void {
  const conversations = db.createObjectStore(STORES.conversations, { keyPath: 'id' })
  conversations.createIndex('updated_at', 'updated_at');
  const messages = db.createObjectStore(STORES.messages, { keyPath: 'id' })
  messages.createIndex('conv', 'conversation_id')
  messages.createIndex('conv_created', ['conversation_id', 'created_at'])
}

function createOutbox(db: IDBDatabase): void {
  if (db.objectStoreNames.contains(STORES.outbox)) return
  const outbox = db.createObjectStore(STORES.outbox, { keyPath: 'id' })
  outbox.createIndex('conv', 'conversation_id')
}

function createSyncTables(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(STORES.events)) {
    db.createObjectStore(STORES.events, { keyPath: 'event_id' })
  }
  if (!db.objectStoreNames.contains(STORES.sync)) {
    db.createObjectStore(STORES.sync, { keyPath: 'id' })
  }
}

let dbPromise: Promise<IDBDatabase> | null = null
let openConn: IDBDatabase | null = null

export async function openChatDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(CHAT_DB_NAME, CHAT_DB_VERSION)
      request.onupgradeneeded = (event) => {
        const db = request.result
        const oldVersion = (event as IDBVersionChangeEvent).oldVersion
        if (oldVersion < 1) createSchema(db)
        // v2: sender outbox (mobile `oldVersion < 2`).
        if (oldVersion < 2) createOutbox(db)
        // v3: read-state columns are schemaless on IDB; sync tables added (mobile `oldVersion < 3`).
        if (oldVersion < 3) createSyncTables(db)
      }
      request.onsuccess = () => {
        openConn = request.result
        resolve(request.result)
      }
      request.onerror = () => reject(request.error)
      request.onblocked = () => reject(new Error('chat database upgrade blocked'))
    })
  }
  return dbPromise
}

/** Test-only: close and drop the shared connection so the next open re-runs upgrades. */
export function closeChatDb(): void {
  try {
    openConn?.close()
  } catch {
    // already closed
  }
  openConn = null
  dbPromise = null
}

async function putAll(store: string, rows: unknown[]): Promise<void> {
  const db = await openChatDb()
  const tx = db.transaction(store, 'readwrite')
  const os = tx.objectStore(store)
  for (const row of rows) os.put(row)
  await txDone(tx)
}

/** Mobile `replaceConversations`: idempotent bulk upsert after a backend fetch. */
export async function replaceConversations(rows: ConversationRow[]): Promise<void> {
  await putAll(STORES.conversations, rows)
}

/** Mobile `upsertConversation`: single-row optimistic update, no full replace. */
export async function upsertConversation(row: ConversationRow): Promise<void> {
  await putAll(STORES.conversations, [row])
}

/** Mobile `getConversations`: cached list, newest-first. */
export async function getConversations(): Promise<ConversationRow[]> {
  const db = await openChatDb()
  const rows = await req(db.transaction(STORES.conversations).objectStore(STORES.conversations).getAll())
  return (rows as ConversationRow[]).sort((a, b) => b.updated_at - a.updated_at)
}

/** Mobile `replaceMessages`: wipe one conversation then bulk-insert. */
export async function replaceMessages(conversationId: string, rows: MessageRow[]): Promise<void> {
  const db = await openChatDb()
  const tx = db.transaction(STORES.messages, 'readwrite')
  const os = tx.objectStore(STORES.messages)
  const existing = await req(os.index('conv').getAll(conversationId))
  for (const row of existing as MessageRow[]) os.delete(row.id)
  for (const row of rows) os.put(row)
  await txDone(tx)
}

/** Mobile `getMessages`: one conversation, newest-first. */
export async function getMessages(conversationId: string): Promise<MessageRow[]> {
  const db = await openChatDb()
  const rows = await req(
    db.transaction(STORES.messages).objectStore(STORES.messages).index('conv').getAll(conversationId),
  )
  return (rows as MessageRow[]).sort((a, b) => b.created_at - a.created_at)
}

/** Mobile `clear`: wipe everything on logout; the sync cursor resets to 0. */
export async function clearChatDb(): Promise<void> {
  const db = await openChatDb()
  const tx = db.transaction(
    [STORES.conversations, STORES.messages, STORES.outbox, STORES.events, STORES.sync],
    'readwrite',
  )
  tx.objectStore(STORES.conversations).clear()
  tx.objectStore(STORES.messages).clear()
  tx.objectStore(STORES.outbox).clear()
  tx.objectStore(STORES.events).clear()
  tx.objectStore(STORES.sync).put({ id: 1, cursor: 0 })
  await txDone(tx)
}

/** Mobile `enqueueOutbox`: the row survives restarts until the server ACKs. */
export async function enqueueOutbox(row: OutboxRow): Promise<void> {
  await putAll(STORES.outbox, [row])
}

/** Mobile `getOutbox`: oldest-first for in-order retry. */
export async function getOutbox(conversationId: string): Promise<OutboxRow[]> {
  const db = await openChatDb()
  const rows = await req(
    db.transaction(STORES.outbox).objectStore(STORES.outbox).index('conv').getAll(conversationId),
  )
  return (rows as OutboxRow[]).sort((a, b) => a.created_at - b.created_at)
}

export async function bumpOutboxAttempts(id: string): Promise<void> {
  const db = await openChatDb()
  const tx = db.transaction(STORES.outbox, 'readwrite')
  const os = tx.objectStore(STORES.outbox)
  const row = (await req(os.get(id))) as OutboxRow | undefined
  if (row) os.put({ ...row, attempts: (row.attempts ?? 0) + 1 })
  await txDone(tx)
}

export async function deleteOutbox(id: string): Promise<void> {
  const db = await openChatDb()
  const tx = db.transaction(STORES.outbox, 'readwrite')
  tx.objectStore(STORES.outbox).delete(id)
  await txDone(tx)
}

/** Every queued row across conversations, oldest-first (reconnect flush). */
export async function getAllOutbox(): Promise<OutboxRow[]> {
  const db = await openChatDb()
  const rows = await req(db.transaction(STORES.outbox).objectStore(STORES.outbox).getAll())
  return (rows as OutboxRow[]).sort((a, b) => a.created_at - b.created_at)
}

export async function getSyncCursor(): Promise<number> {
  const db = await openChatDb()
  const os = db.transaction(STORES.sync).objectStore(STORES.sync)
  const row = ((await req(os.get(1))) as { cursor: number } | undefined) ??
    ((await req(os.get('1'))) as { cursor: number } | undefined)
  return row?.cursor ?? 0
}

/**
 * Mobile `applyEvent`: persist the event payload and advance the cursor in
 * one transaction. A duplicate `eventId` is a no-op returning false.
 */
export async function applyEvent(eventId: string, cursor: number, message?: MessageRow): Promise<boolean> {
  const db = await openChatDb()
  const tx = db.transaction([STORES.messages, STORES.events, STORES.sync], 'readwrite')
  if (message) tx.objectStore(STORES.messages).put(message)
  const addReq = tx.objectStore(STORES.events).add({ event_id: eventId, cursor })
  const added: boolean = await new Promise((resolve) => {
    addReq.onsuccess = () => resolve(true)
    addReq.onerror = () => {
      // Duplicate event: abort this attempt, the cursor stays put.
      try {
        tx.abort()
      } catch {
        // already finished
      }
      resolve(false)
    }
  })
  if (!added) return false
  const syncOs = tx.objectStore(STORES.sync)
  const current = ((await req(syncOs.get(1))) as { cursor: number } | undefined) ??
    ((await req(syncOs.get('1'))) as { cursor: number } | undefined)
  const next = Math.max(current?.cursor ?? 0, cursor)
  syncOs.put({ id: 1, cursor: next })
  await txDone(tx)
  return true
}
